import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutGrid,
  Inbox,
  Activity,
  Search,
  RefreshCw,
  Trash2,
  Copy,
  Mail,
  MailCheck,
  Send,
  Bell,
  ChevronRight,
  LogOut,
  Settings,
  ShieldCheck,
  ArrowLeft,
  Menu,
  Radio,
  Cpu,
  Signal,
  CheckCircle2,
  XCircle,
  AlertCircle,
  AlertTriangle,
  Clock,
  Bot,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "@/components/ui/sheet";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { useSeo } from "@/hooks/useSeo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OPERATOR = {
  name: "Chief Architect",
  role: "Root Operator",
  initials: "CA",
};

const NAV = [
  { id: "overview", label: "Command Overview", icon: LayoutGrid },
  { id: "leads", label: "Lead Signals", icon: Inbox },
  { id: "alerts", label: "System Alerts", icon: AlertTriangle },
  { id: "activity", label: "Activity Log", icon: Activity },
  { id: "diagnostics", label: "Email Diagnostics", icon: MailCheck },
];

const STATUS_OPTIONS = ["new", "contacted", "qualified", "closed"];
const statusColor = {
  new: "text-neon-g border-[color:var(--stroke-g)] bg-[rgba(57,255,20,0.06)]",
  contacted: "text-neon-p border-[color:var(--stroke-p)] bg-[rgba(160,32,240,0.06)]",
  qualified: "text-[#F5C542] border-[rgba(245,197,66,0.3)] bg-[rgba(245,197,66,0.06)]",
  closed: "text-muted-brand border-[rgba(255,255,255,0.12)] bg-[rgba(255,255,255,0.03)]",
};

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const relTime = (iso) => {
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  } catch {
    return "";
  }
};

export default function Admin() {
  useSeo({
    title: "Métis Command Center",
    description: "Métis Command Center — the internal intelligence hub for Métis Tech Labs.",
  });

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const location = useLocation();
  const [view, setView] = useState(location.state?.view || "overview");
  const [sessionStart] = useState(() => new Date());

  // Allow deep-links from the Métis page (e.g. "Lead Signals") to select a view.
  useEffect(() => {
    if (location.state?.view) setView(location.state.view);
  }, [location.state]);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await axios.get(`${API}/contact`);
      setItems(res.data || []);
    } catch (e) {
      if (process.env.NODE_ENV === "development") console.error(e);
      toast.error("Signal link failed — retrying shortly.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) =>
      [it.name, it.email, it.company, it.project_type, it.message]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(q))
    );
  }, [items, query]);

  const newCount = items.filter((i) => i.status === "new").length;

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/contact/${id}`, { status });
      setItems((prev) => prev.map((it) => (it.id === id ? { ...it, status } : it)));
      if (selected?.id === id) setSelected({ ...selected, status });
      toast.success(`Signal reclassified — ${status}`);
    } catch (e) {
      toast.error("Failed to update signal status");
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/contact/${id}`);
      setItems((prev) => prev.filter((it) => it.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success("Signal purged from the grid");
    } catch (e) {
      toast.error("Failed to purge signal");
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const activeNav = NAV.find((n) => n.id === view);

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-[color:var(--bg-0)] noise">
        <div className="relative flex h-20 w-20 items-center justify-center">
          <span className="absolute inset-0 animate-ping rounded-full bg-[rgba(57,255,20,0.15)]" />
          <span className="absolute inset-0 rounded-full border border-[color:var(--stroke-g)]" />
          <Cpu className="text-neon-g" size={28} />
        </div>
        <p className="mt-6 font-display text-lg font-semibold tracking-tight text-white">
          Initializing Command Center
        </p>
        <p className="mt-1 text-sm text-muted-brand">Establishing secure signal link…</p>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-[color:var(--bg-0)] noise text-white">
      {/* SIDEBAR (desktop) */}
      <Sidebar
        view={view}
        setView={setView}
        newCount={newCount}
        sessionStart={sessionStart}
        className="hidden lg:flex"
      />

      {/* MAIN */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Top bar */}
        <header className="sticky top-0 z-30 border-b border-[rgba(255,255,255,0.06)] bg-[rgba(11,11,13,0.72)] backdrop-blur-xl">
          <div className="flex items-center justify-between gap-3 px-4 py-3 sm:px-6">
            <div className="flex items-center gap-3">
              {/* Mobile menu */}
              <Sheet>
                <SheetTrigger asChild>
                  <button
                    className="flex h-9 w-9 items-center justify-center rounded-lg glass lg:hidden"
                    aria-label="Open navigation"
                    data-testid="cc-mobile-menu-button"
                  >
                    <Menu size={17} />
                  </button>
                </SheetTrigger>
                <SheetContent side="left" className="w-72 border-r border-[rgba(255,255,255,0.08)] bg-[color:var(--bg-1)] p-0">
                  <Sidebar view={view} setView={setView} newCount={newCount} sessionStart={sessionStart} mobile />
                </SheetContent>
              </Sheet>

              {/* Breadcrumbs */}
              <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-sm" data-testid="cc-breadcrumbs">
                <span className="text-muted-brand">Métis Command Center</span>
                <ChevronRight size={14} className="text-muted-brand" />
                <span className="font-medium text-white">{activeNav?.label}</span>
              </nav>
            </div>

            <div className="flex items-center gap-2">
              <span className="hidden items-center gap-1.5 rounded-full glass px-3 py-1.5 text-xs text-neon-g sm:flex">
                <Signal size={13} /> Live
              </span>

              {/* Notifications */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="relative flex h-9 w-9 items-center justify-center rounded-lg glass text-white transition-all hover:shadow-[var(--glow-g)]"
                    aria-label="Notifications"
                    data-testid="cc-notifications-button"
                  >
                    <Bell size={16} />
                    {newCount > 0 && (
                      <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-[color:var(--neon-g)] px-1 text-[10px] font-bold text-black">
                        {newCount}
                      </span>
                    )}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-72 border-[rgba(255,255,255,0.1)] bg-[color:var(--bg-1)] text-white">
                  <DropdownMenuLabel className="text-muted-brand">Signal notifications</DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-[rgba(255,255,255,0.08)]" />
                  {items.slice(0, 4).length === 0 ? (
                    <div className="px-3 py-6 text-center text-sm text-muted-brand">No signals yet.</div>
                  ) : (
                    items.slice(0, 4).map((it) => (
                      <DropdownMenuItem key={it.id} className="flex-col items-start gap-0.5 focus:bg-[rgba(255,255,255,0.05)]" onClick={() => { setView("leads"); setSelected(it); }}>
                        <span className="text-sm text-white">New signal — {it.name}</span>
                        <span className="text-xs text-muted-brand">{it.project_type || "General"} · {relTime(it.created_at)}</span>
                      </DropdownMenuItem>
                    ))
                  )}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Account dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 rounded-full glass py-1 pl-1 pr-3 transition-all hover:shadow-[var(--glow-g)]" data-testid="cc-account-button">
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[rgba(57,255,20,0.12)] text-xs font-bold text-neon-g">
                      {OPERATOR.initials}
                    </span>
                    <span className="hidden text-sm font-medium sm:block">{OPERATOR.name}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-60 border-[rgba(255,255,255,0.1)] bg-[color:var(--bg-1)] text-white">
                  <div className="flex items-center gap-3 px-2 py-2">
                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[rgba(160,32,240,0.14)] text-sm font-bold text-neon-p">
                      {OPERATOR.initials}
                    </span>
                    <div>
                      <div className="text-sm font-semibold">{OPERATOR.name}</div>
                      <div className="text-xs text-muted-brand">{OPERATOR.role}</div>
                    </div>
                  </div>
                  <DropdownMenuSeparator className="bg-[rgba(255,255,255,0.08)]" />
                  <DropdownMenuItem className="focus:bg-[rgba(255,255,255,0.05)]"><ShieldCheck size={15} className="mr-2 text-neon-g" /> Full access</DropdownMenuItem>
                  <DropdownMenuItem className="focus:bg-[rgba(255,255,255,0.05)]"><Settings size={15} className="mr-2 text-muted-brand" /> Preferences</DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-[rgba(255,255,255,0.08)]" />
                  <DropdownMenuItem asChild className="focus:bg-[rgba(255,255,255,0.05)]">
                    <Link to="/"><ArrowLeft size={15} className="mr-2 text-muted-brand" /> Exit to site</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem className="text-[#ff6b6b] focus:bg-[rgba(255,107,107,0.08)]"><LogOut size={15} className="mr-2" /> End session</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Content */}
        <div className="flex-1 px-4 py-6 sm:px-6 lg:px-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={view}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              {view === "overview" && (
                <Overview items={items} newCount={newCount} onOpen={(it) => { setView("leads"); setSelected(it); }} onRefresh={() => load()} />
              )}
              {view === "leads" && (
                <Leads
                  items={items}
                  filtered={filtered}
                  query={query}
                  setQuery={setQuery}
                  setSelected={setSelected}
                  copy={copy}
                  remove={remove}
                  onRefresh={() => load()}
                />
              )}
              {view === "activity" && <ActivityLog items={items} />}
              {view === "alerts" && <SystemAlerts />}
              {view === "diagnostics" && <Diagnostics />}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg border-[rgba(255,255,255,0.1)] bg-[color:var(--bg-1)] text-white">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-xl">{selected.name}</DialogTitle>
                <DialogDescription className="text-muted-brand">
                  Full signal details for the selected lead.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <Row label="Email" value={selected.email} />
                <Row label="Company" value={selected.company || "—"} />
                <Row label="Phone" value={selected.phone || "—"} />
                <Row label="Project Type" value={selected.project_type || "—"} />
                <Row label="Budget" value={selected.budget || "—"} />
                <div>
                  <div className="text-xs uppercase tracking-[0.14em] text-muted-brand">Message</div>
                  <p className="mt-1 whitespace-pre-wrap leading-relaxed text-white">{selected.message}</p>
                </div>
                <div className="flex items-center gap-3 border-t border-[rgba(255,255,255,0.08)] pt-4">
                  <span className="text-xs uppercase tracking-[0.14em] text-muted-brand">Status</span>
                  <Select value={selected.status} onValueChange={(v) => updateStatus(selected.id, v)}>
                    <SelectTrigger className="brand-input h-9 w-40" data-testid="cc-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map((s) => (
                        <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Toaster position="bottom-right" theme="dark" />
    </div>
  );
}

/* ---------------- Sidebar ---------------- */
function Sidebar({ view, setView, newCount, sessionStart, className = "", mobile = false }) {
  return (
    <aside className={`${className} w-64 shrink-0 flex-col border-r border-[rgba(255,255,255,0.06)] bg-[color:var(--bg-1)] ${mobile ? "flex h-full" : ""}`}>
      <div className="flex items-center gap-2 px-5 py-5">
        <Logo showText={false} />
        <div className="leading-tight">
          <div className="font-display text-sm font-semibold text-white">Métis</div>
          <div className="text-[11px] uppercase tracking-[0.16em] text-neon-g">Command Center</div>
        </div>
      </div>

      <nav className="flex-1 space-y-1 px-3 py-2" aria-label="Command Center navigation">
        {NAV.map((n) => {
          const active = view === n.id;
          return (
            <button
              key={n.id}
              onClick={() => setView(n.id)}
              data-testid={`cc-nav-${n.id}`}
              className={`flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-sm transition-all duration-300 ${
                active
                  ? "glass text-white shadow-[var(--glow-g)] border border-[color:var(--stroke-g)]"
                  : "text-muted-brand hover:bg-[rgba(255,255,255,0.03)] hover:text-white"
              }`}
            >
              <span className="flex items-center gap-3">
                <n.icon size={17} className={active ? "text-neon-g" : ""} />
                {n.label}
              </span>
              {n.id === "leads" && newCount > 0 && (
                <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-[rgba(57,255,20,0.14)] px-1.5 text-[10px] font-bold text-neon-g">
                  {newCount}
                </span>
              )}
            </button>
          );
        })}

        {/* Flagship AI module — separate route */}
        <div className="my-2 border-t border-[rgba(255,255,255,0.06)]" />
        <Link
          to="/admin/metis"
          data-testid="cc-nav-metis"
          className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-muted-brand transition-all duration-300 hover:bg-[rgba(160,32,240,0.06)] hover:text-white"
        >
          <Bot size={17} className="text-neon-p" />
          <span>Métis</span>
          <span className="ml-auto rounded-full bg-[rgba(160,32,240,0.16)] px-2 py-0.5 text-[10px] font-semibold text-neon-p">
            AI
          </span>
        </Link>
      </nav>

      {/* Operator profile / session */}
      <div className="m-3 rounded-2xl glass p-4">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[rgba(57,255,20,0.12)] text-sm font-bold text-neon-g">
            {OPERATOR.initials}
          </span>
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold text-white">{OPERATOR.name}</div>
            <div className="flex items-center gap-1 text-[11px] text-neon-g">
              <ShieldCheck size={11} /> {OPERATOR.role}
            </div>
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between border-t border-[rgba(255,255,255,0.08)] pt-3 text-[11px] text-muted-brand">
          <span className="flex items-center gap-1"><Radio size={11} className="text-neon-g" /> Session</span>
          <span>{sessionStart.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}</span>
        </div>
      </div>
    </aside>
  );
}

/* ---------------- Overview ---------------- */
function Overview({ items, newCount, onOpen, onRefresh }) {
  const stats = [
    { label: "Total Signals", value: items.length, color: "green" },
    { label: "New", value: newCount, color: "green" },
    { label: "Contacted", value: items.filter((i) => i.status === "contacted").length, color: "purple" },
    { label: "Closed", value: items.filter((i) => i.status === "closed").length, color: "gray" },
  ];
  const recent = items.slice(0, 5);

  return (
    <div>
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight sm:text-3xl">
            Welcome back, {OPERATOR.name}.
          </h1>
          <p className="mt-1 text-sm text-muted-brand">
            You are overseeing AI systems, automations, and client solutions from the central hub.
          </p>
        </div>
        <button onClick={onRefresh} data-testid="cc-refresh-button" className="inline-flex w-fit items-center gap-2 rounded-lg glass px-3 py-2 text-sm text-white transition-all hover:shadow-[var(--glow-g)]">
          <RefreshCw size={15} /> Sync grid
        </button>
      </div>

      <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-4">
        {stats.map((s) => (
          <div key={s.label} className="neon-frame rounded-2xl glass p-5">
            <div className="font-display text-2xl font-semibold text-white sm:text-3xl">{s.value}</div>
            <div className="mt-1 text-xs text-muted-brand">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="mt-8 rounded-2xl glass p-5">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-display text-lg font-semibold text-white">Recent signals</h2>
          <span className="text-xs text-muted-brand">Latest 5</span>
        </div>
        {recent.length === 0 ? (
          <EmptyState />
        ) : (
          <ul className="space-y-2">
            {recent.map((it) => (
              <li key={it.id}>
                <button onClick={() => onOpen(it)} className="flex w-full items-center justify-between rounded-xl px-3 py-3 text-left transition-colors hover:bg-[rgba(255,255,255,0.03)]">
                  <span className="flex items-center gap-3">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[rgba(160,32,240,0.12)] text-xs font-bold text-neon-p">
                      {(it.name || "?").slice(0, 2).toUpperCase()}
                    </span>
                    <span>
                      <span className="block text-sm font-medium text-white">{it.name}</span>
                      <span className="block text-xs text-muted-brand">{it.project_type || "General"} · {relTime(it.created_at)}</span>
                    </span>
                  </span>
                  <span className={`rounded-full border px-2.5 py-1 text-[11px] capitalize ${statusColor[it.status] || statusColor.new}`}>{it.status}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/* ---------------- Leads ---------------- */
function Leads({ items, filtered, query, setQuery, setSelected, copy, remove, onRefresh }) {
  return (
    <div>
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight">Lead Signals</h1>
          <p className="mt-1 text-sm text-muted-brand">Every inbound consultation request, captured on the grid.</p>
        </div>
        <button onClick={onRefresh} data-testid="cc-leads-refresh-button" className="inline-flex w-fit items-center gap-2 rounded-lg glass px-3 py-2 text-sm text-white transition-all hover:shadow-[var(--glow-g)]">
          <RefreshCw size={15} /> Sync
        </button>
      </div>

      <div className="mt-6 max-w-md">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-brand" />
          <Input
            data-testid="cc-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Scan signals by name, email, company, project…"
            className="brand-input pl-9"
          />
        </div>
      </div>

      <div className="mt-6 overflow-hidden rounded-2xl glass" data-testid="cc-submissions-table">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-[rgba(255,255,255,0.08)] hover:bg-transparent">
                <TableHead className="text-muted-brand">Signal</TableHead>
                <TableHead className="text-muted-brand">Company</TableHead>
                <TableHead className="text-muted-brand">Project</TableHead>
                <TableHead className="text-muted-brand">Status</TableHead>
                <TableHead className="text-muted-brand">Received</TableHead>
                <TableHead className="text-right text-muted-brand">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="py-16">
                    <EmptyState query={query} />
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((it) => (
                  <TableRow
                    key={it.id}
                    className="cursor-pointer border-[rgba(255,255,255,0.06)] transition-colors hover:bg-[rgba(255,255,255,0.03)]"
                    onClick={() => setSelected(it)}
                    data-testid={`cc-row-${it.id}`}
                  >
                    <TableCell>
                      <div className="font-medium text-white">{it.name}</div>
                      <div className="text-xs text-muted-brand">{it.email}</div>
                    </TableCell>
                    <TableCell className="text-muted-brand">{it.company || "—"}</TableCell>
                    <TableCell className="text-muted-brand">{it.project_type || "—"}</TableCell>
                    <TableCell>
                      <span className={`rounded-full border px-2.5 py-1 text-[11px] capitalize ${statusColor[it.status] || statusColor.new}`}>{it.status}</span>
                    </TableCell>
                    <TableCell className="text-xs text-muted-brand">{fmtDate(it.created_at)}</TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1.5">
                        <button title="Copy email" onClick={() => copy(it.email)} className="rounded-lg p-2 text-muted-brand transition-colors hover:text-neon-g">
                          <Copy size={15} />
                        </button>
                        <a title="Email" href={`mailto:${it.email}`} className="rounded-lg p-2 text-muted-brand transition-colors hover:text-neon-g">
                          <Mail size={15} />
                        </a>
                        <button title="Purge" onClick={() => remove(it.id)} data-testid={`cc-delete-${it.id}`} className="rounded-lg p-2 text-muted-brand transition-colors hover:text-[#ff6b6b]">
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Activity Log ---------------- */
function ActivityLog({ items }) {
  const events = items.map((it) => ({
    id: it.id,
    title: `New signal received — ${it.name}`,
    meta: `${it.project_type || "General inquiry"} · ${it.company || "Independent"}`,
    time: it.created_at,
    status: it.status,
  }));

  return (
    <div>
      <h1 className="font-display text-2xl font-semibold tracking-tight">Activity Log</h1>
      <p className="mt-1 text-sm text-muted-brand">Chronological record of signals across the grid.</p>

      <div className="mt-6 rounded-2xl glass p-6">
        {events.length === 0 ? (
          <EmptyState />
        ) : (
          <ol className="relative space-y-6 border-l border-[rgba(255,255,255,0.1)] pl-6">
            {events.map((e) => (
              <li key={e.id} className="relative">
                <span className="absolute -left-[29px] top-1 flex h-3 w-3 items-center justify-center rounded-full bg-[color:var(--neon-g)] shadow-[0_0_10px_var(--neon-g)]" />
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-sm font-medium text-white">{e.title}</span>
                  <span className="text-xs text-muted-brand">{fmtDate(e.time)}</span>
                </div>
                <div className="mt-1 text-xs text-muted-brand">{e.meta}</div>
                <span className={`mt-2 inline-block rounded-full border px-2.5 py-0.5 text-[11px] capitalize ${statusColor[e.status] || statusColor.new}`}>{e.status}</span>
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}

/* ---------------- System Alerts ---------------- */
const SEVERITY_META = {
  info: { label: "Info", cls: "text-neon-g border-[color:var(--stroke-g)] bg-[rgba(57,255,20,0.06)]" },
  warning: { label: "Warning", cls: "text-[#F5C542] border-[rgba(245,197,66,0.3)] bg-[rgba(245,197,66,0.06)]" },
  critical: { label: "Critical", cls: "text-[#ff6b6b] border-[rgba(255,107,107,0.3)] bg-[rgba(255,107,107,0.06)]" },
};
const ALERT_STATUS_META = {
  open: "text-[#ff6b6b] border-[rgba(255,107,107,0.3)] bg-[rgba(255,107,107,0.06)]",
  acknowledged: "text-neon-p border-[color:var(--stroke-p)] bg-[rgba(160,32,240,0.06)]",
  resolved: "text-muted-brand border-[rgba(255,255,255,0.12)] bg-[rgba(255,255,255,0.03)]",
};
const ALERT_STATUSES = ["open", "acknowledged", "resolved"];

function SystemAlerts() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/alerts`);
      setAlerts(res.data || []);
    } catch (e) {
      toast.error("Failed to load alerts");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/alerts/${id}`, { status });
      setAlerts((prev) => prev.map((a) => (a.id === id ? { ...a, status } : a)));
      toast.success(`Alert ${status}`);
    } catch (e) {
      toast.error("Failed to update alert");
    }
  };

  const fmt = (iso) => {
    try {
      return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch {
      return iso;
    }
  };

  const counts = {
    open: alerts.filter((a) => a.status === "open").length,
    critical: alerts.filter((a) => a.severity === "critical" && a.status !== "resolved").length,
    total: alerts.length,
  };
  const filtered = filter === "all" ? alerts : alerts.filter((a) => a.status === filter);

  return (
    <div data-testid="cc-alerts-panel">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight">System Alerts</h1>
          <p className="mt-1 text-sm text-muted-brand">
            Automatically generated from real system events across services.
          </p>
        </div>
        <button onClick={fetchAlerts} data-testid="cc-alerts-refresh-button" className="inline-flex w-fit items-center gap-2 rounded-lg glass px-3 py-2 text-sm text-white transition-all hover:shadow-[var(--glow-g)]">
          <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      <div className="mt-6 grid grid-cols-3 gap-4">
        <div className="neon-frame rounded-2xl glass p-5">
          <div className="font-display text-2xl font-semibold text-white sm:text-3xl">{counts.open}</div>
          <div className="mt-1 text-xs text-muted-brand">Open</div>
        </div>
        <div className="neon-frame rounded-2xl glass p-5">
          <div className="font-display text-2xl font-semibold text-[#ff6b6b] sm:text-3xl">{counts.critical}</div>
          <div className="mt-1 text-xs text-muted-brand">Critical (unresolved)</div>
        </div>
        <div className="neon-frame rounded-2xl glass p-5">
          <div className="font-display text-2xl font-semibold text-white sm:text-3xl">{counts.total}</div>
          <div className="mt-1 text-xs text-muted-brand">Total</div>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        {["all", ...ALERT_STATUSES].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            data-testid={`cc-alerts-filter-${f}`}
            className={`rounded-full px-4 py-1.5 text-sm capitalize transition-all ${
              filter === f ? "glass text-white border border-[color:var(--stroke-g)] shadow-[var(--glow-g)]" : "glass text-muted-brand hover:text-white"
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      <div className="mt-5 space-y-3">
        {loading ? (
          <div className="py-10 text-center text-muted-brand">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="rounded-2xl glass py-12">
            <div className="flex flex-col items-center gap-3 text-muted-brand" data-testid="cc-alerts-empty">
              <div className="flex h-14 w-14 items-center justify-center rounded-full glass">
                <ShieldCheck size={22} className="text-neon-g" />
              </div>
              <p className="text-sm">All clear — no {filter !== "all" ? filter : ""} alerts.</p>
            </div>
          </div>
        ) : (
          filtered.map((a) => {
            const sev = SEVERITY_META[a.severity] || SEVERITY_META.warning;
            return (
              <div key={a.id} data-testid={`cc-alert-row-${a.id}`} className="rounded-2xl glass p-5 hover-lift">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`rounded-full border px-2.5 py-0.5 text-[11px] ${sev.cls}`}>{sev.label}</span>
                      <span className="rounded-full border border-[rgba(255,255,255,0.12)] px-2.5 py-0.5 text-[11px] text-muted-brand">{a.source}</span>
                      {a.count > 1 && (
                        <span className="rounded-full border border-[rgba(255,255,255,0.12)] px-2.5 py-0.5 text-[11px] text-muted-brand">×{a.count}</span>
                      )}
                    </div>
                    <h3 className="mt-2 font-medium text-white">{a.title}</h3>
                    <p className="mt-1 break-words text-sm text-muted-brand">{a.message}</p>
                    <div className="mt-2 flex items-center gap-1.5 text-xs text-muted-brand">
                      <Clock size={12} /> {fmt(a.created_at)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`hidden rounded-full border px-2.5 py-1 text-[11px] capitalize sm:inline ${ALERT_STATUS_META[a.status]}`}>{a.status}</span>
                    <Select value={a.status} onValueChange={(v) => setStatus(a.id, v)}>
                      <SelectTrigger className="brand-input h-9 w-36" data-testid={`cc-alert-status-${a.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ALERT_STATUSES.map((s) => (
                          <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

/* ---------------- Email Diagnostics ---------------- */
const CONNECTION_META = {
  connected: { label: "Connected", cls: statusColor.new, Icon: CheckCircle2 },
  ready: { label: "Ready", cls: statusColor.contacted, Icon: Signal },
  error: { label: "Error", cls: "text-[#ff6b6b] border-[rgba(255,107,107,0.3)] bg-[rgba(255,107,107,0.06)]", Icon: XCircle },
  not_configured: { label: "Not configured", cls: statusColor.closed, Icon: AlertCircle },
  unknown: { label: "Unknown", cls: statusColor.closed, Icon: AlertCircle },
};

function Diagnostics() {
  const [diag, setDiag] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testTo, setTestTo] = useState("");
  const [sending, setSending] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const fetchDiag = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/email/diagnostics`);
      setDiag(res.data);
    } catch (e) {
      toast.error("Failed to load diagnostics");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDiag();
  }, [fetchDiag]);

  const sendTest = async () => {
    setSending(true);
    setTestResult(null);
    try {
      const res = await axios.post(`${API}/email/test`, { to: testTo.trim() || null });
      setTestResult(res.data.result);
      if (res.data.diagnostics) setDiag(res.data.diagnostics);
      if (res.data.result?.ok) toast.success(`Test email sent to ${res.data.result.recipient}`);
      else toast.error("Test email failed — see result below");
    } catch (e) {
      const detail = e?.response?.data?.detail || "Request failed";
      setTestResult({ ok: false, recipient: testTo, error: detail });
      toast.error("Test email failed");
    } finally {
      setSending(false);
    }
  };

  const meta = CONNECTION_META[diag?.connection_status] || CONNECTION_META.unknown;

  const fmt = (iso) => {
    if (!iso) return "Never";
    try {
      return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch {
      return iso;
    }
  };

  return (
    <div data-testid="cc-diagnostics-panel">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight">Email Diagnostics</h1>
          <p className="mt-1 text-sm text-muted-brand">Transport health for lead notifications, confirmations, and system alerts.</p>
        </div>
        <button onClick={fetchDiag} data-testid="cc-diag-refresh-button" className="inline-flex w-fit items-center gap-2 rounded-lg glass px-3 py-2 text-sm text-white transition-all hover:shadow-[var(--glow-g)]">
          <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {/* Status grid */}
      <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="neon-frame rounded-2xl glass p-5">
          <div className="text-xs uppercase tracking-[0.14em] text-muted-brand">Provider</div>
          <div className="mt-2 flex items-center gap-2">
            <Cpu size={18} className="text-neon-g" />
            <span className="font-display text-lg font-semibold text-white" data-testid="cc-diag-provider">
              {diag?.provider_label || "—"}
            </span>
          </div>
          <div className="mt-3 flex flex-wrap gap-2 text-[11px]">
            <span className={`rounded-full border px-2.5 py-1 ${diag?.enabled ? statusColor.new : statusColor.closed}`}>
              {diag?.enabled ? "Notifications ON" : "Notifications OFF"}
            </span>
            <span className={`rounded-full border px-2.5 py-1 ${diag?.configured ? statusColor.new : statusColor.closed}`}>
              {diag?.configured ? "Credentials set" : "Credentials missing"}
            </span>
          </div>
        </div>

        <div className="neon-frame rounded-2xl glass p-5">
          <div className="text-xs uppercase tracking-[0.14em] text-muted-brand">Connection Status</div>
          <div className="mt-2 flex items-center gap-2">
            <meta.Icon size={18} className={diag?.connection_status === "connected" ? "text-neon-g" : diag?.connection_status === "error" ? "text-[#ff6b6b]" : "text-muted-brand"} />
            <span className={`rounded-full border px-3 py-1 text-sm capitalize ${meta.cls}`} data-testid="cc-diag-status">
              {meta.label}
            </span>
          </div>
          <div className="mt-3 flex items-center gap-2 text-xs text-muted-brand">
            <Clock size={13} /> Last successful send: <span className="text-white">{fmt(diag?.last_success_at)}</span>
          </div>
        </div>
      </div>

      {/* Routing */}
      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <RouteTile label="From" value={diag?.mail_from} />
        <RouteTile label="Lead notifications → " value={diag?.notify_to} />
        <RouteTile label="System alerts → " value={diag?.alerts_to} />
      </div>

      {/* Send test */}
      <div className="mt-6 rounded-2xl glass p-6">
        <h2 className="font-display text-lg font-semibold text-white">Send Test Email</h2>
        <p className="mt-1 text-sm text-muted-brand">
          Verify delivery without using the public form. Leave blank to send to the default notification address.
        </p>
        <div className="mt-4 flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-brand" />
            <Input
              data-testid="cc-test-email-input"
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
              placeholder={diag?.notify_to || "recipient@example.com"}
              className="brand-input pl-9"
            />
          </div>
          <button
            onClick={sendTest}
            disabled={sending}
            data-testid="cc-send-test-button"
            className="pulse-glow inline-flex items-center justify-center gap-2 rounded-lg bg-[color:var(--neon-g)] px-5 py-2.5 text-sm font-semibold text-black transition-all hover:brightness-110 disabled:opacity-50"
          >
            {sending ? "Sending…" : (<>Send Test <Send size={15} /></>)}
          </button>
        </div>

        {testResult && (
          <div
            data-testid="cc-test-result"
            className={`mt-4 flex items-start gap-3 rounded-xl border p-4 text-sm ${
              testResult.ok
                ? "border-[color:var(--stroke-g)] bg-[rgba(57,255,20,0.06)]"
                : "border-[rgba(255,107,107,0.3)] bg-[rgba(255,107,107,0.06)]"
            }`}
          >
            {testResult.ok ? <CheckCircle2 size={18} className="mt-0.5 text-neon-g" /> : <XCircle size={18} className="mt-0.5 text-[#ff6b6b]" />}
            <div>
              <div className="font-medium text-white">
                {testResult.ok ? `Test email sent to ${testResult.recipient}` : "Test email failed"}
              </div>
              {testResult.error && <div className="mt-1 text-muted-brand">{testResult.error}</div>}
            </div>
          </div>
        )}

        {/* Last recorded test */}
        {diag?.last_test && (
          <div className="mt-5 border-t border-[rgba(255,255,255,0.08)] pt-4 text-xs text-muted-brand">
            <span className="uppercase tracking-[0.14em]">Most recent test</span>
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
              <span className="flex items-center gap-1.5">
                {diag.last_test.ok ? <CheckCircle2 size={13} className="text-neon-g" /> : <XCircle size={13} className="text-[#ff6b6b]" />}
                {diag.last_test.ok ? "Success" : "Failed"}
              </span>
              <span>To: <span className="text-white">{diag.last_test.recipient || "—"}</span></span>
              <span>At: <span className="text-white">{fmt(diag.last_test.at)}</span></span>
            </div>
            {diag.last_test.error && <div className="mt-1 text-[#ff9b9b]">{diag.last_test.error}</div>}
          </div>
        )}
      </div>
    </div>
  );
}

const RouteTile = ({ label, value }) => (
  <div className="rounded-2xl glass p-4">
    <div className="text-xs uppercase tracking-[0.12em] text-muted-brand">{label}</div>
    <div className="mt-1.5 truncate text-sm text-white">{value || "—"}</div>
  </div>
);

/* ---------------- Shared ---------------- */
function EmptyState({ query }) {
  return (
    <div className="flex flex-col items-center gap-3 py-10 text-center text-muted-brand" data-testid="cc-empty-state">
      <div className="flex h-14 w-14 items-center justify-center rounded-full glass">
        <Radio size={22} className="text-neon-g" />
      </div>
      <p className="text-sm">
        {query ? "No signals match your scan." : "No signals detected yet — the grid is clear."}
      </p>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-4">
    <span className="text-xs uppercase tracking-[0.14em] text-muted-brand">{label}</span>
    <span className="text-right text-white">{value}</span>
  </div>
);
